import React, { useMemo, useState } from "react";
import { AbsoluteFill, Sequence, staticFile } from "remotion";
import { linearTiming, TransitionSeries } from "@remotion/transitions";
import { slide } from "@remotion/transitions/slide";
import { fade } from "@remotion/transitions/fade";
import { z } from "zod";
import { FourPanelReveal } from "./FourPanelReveal";
import { PopUpText } from "../Overlay/PopUpText";
import { MagnifyingGlass } from "./MagnifyingGlass";
import { Blur } from "../Background/Blur";
import CroppedVideo from "../components/CroppedVideo";
import { resolveMediaSrc } from "../utils/mediaSrc";

/**
 * Calculate metadata for Furniture composition with automatic video duration
 * This function can be used in Root.tsx to automatically set composition duration
 */
export const calculateFurnitureMetadata = async ({ props, defaultProps }) => {
  const mergedProps = { ...defaultProps, ...props };
  const { 
    includeVideo, 
    videoSrc, 
    autoCalculateVideoDuration,
    fourPanelDuration, 
    transitionDuration, 
    magnifyingGlassDuration,
    videoDuration,
    includeMagnifyingGlass 
  } = mergedProps;
  
  let totalDuration = fourPanelDuration; // Base duration
  
  // Add MagnifyingGlass duration if enabled
  if (includeMagnifyingGlass) {
    totalDuration +=  magnifyingGlassDuration - transitionDuration;
  }
  
  // Add Video duration (calculated or manual)
  if (includeVideo) {
    let actualVideoDuration = videoDuration;
    
    if (autoCalculateVideoDuration) {
      try {
        // Import parseMedia here to avoid issues if @remotion/media-utils isn't installed
        const { parseMedia } = await import("@remotion/media-utils");
        
        // Get video source (handle local vs remote)
        const videoSource = resolveMediaSrc(videoSrc);
        if (!videoSource) {
          throw new Error("Invalid videoSrc provided for metadata calculation");
        }
          
        const metadata = await parseMedia({
          src: videoSource,
          fields: {
            durationInSeconds: true,
          }
        });
        
        // Convert to frames (assuming 30fps, but you could make this configurable)
        actualVideoDuration = Math.ceil(metadata.durationInSeconds * 30);
        
      } catch (error) {
        console.warn('Could not calculate video duration, using manual duration:', error);
        actualVideoDuration = videoDuration;
      }
    }
    
    totalDuration += actualVideoDuration - transitionDuration;
  }
  
  return {
    durationInFrames: totalDuration,
  };
};

// Zod schema for Furniture props
export const FurniturePropsSchema = z.object({
  // FourPanelReveal props
  imageSrc: z.string().default("1.jpeg"),
  videoSrc: z.string().default("0902.mp4").describe("Video file path (local) or URL (remote)"),
  popupTitle: z.string().default("Amazing Reveal!"),
  popupSubtitle: z.string().default("Four Panel Effect"),

  blurAmount: z.number().default(10),
  pulseScale: z.number().default(1.2),
  flashIntensity: z.number().default(0.8),
  flashDuration: z.number().default(8),

  // PopUpText props

  popupStartFrame: z.number().default(30),
  popupInDuration: z.number().default(20),
  popupVisibleForFrames: z.number().default(120),
  popupOutDuration: z.number().default(15),
  popupX: z.number().default(80),
  popupY: z.number().default(80),
  popupMaxWidth: z.number().default(600),
  
  // MagnifyingGlass props and toggle
  includeMagnifyingGlass: z.boolean().default(false),
  magnifierX: z.number().default(50),
  magnifierY: z.number().default(50),
  magnifierRadius: z.number().default(200),
  magnificationLevel: z.number().default(2.5),
  magnifierBorderWidth: z.number().default(4),
  magnifierBorderColor: z.string().default("#ffffff"),
  magnifierShadowBlur: z.number().default(20),
  magnifierShadowColor: z.string().default("rgba(0, 0, 0, 0.3)"),
  backgroundZoomStart: z.number().default(1),
  backgroundZoomEnd: z.number().default(1.5),
  backgroundZoomDuration: z.number().default(200),
  magnifyingGlassEasingType: z.enum(["linear", "easeIn", "easeOut", "easeInOut"]).default("easeOut"),
  
  // Video props and toggle
  includeVideo: z.boolean().default(false),
  videoBackground: z.boolean().default(false),
  videoStartFrom: z.number().default(0),
  videoEndAt: z.number().optional().describe("For video: stop playback at this frame (trim end)"),
  videoObjectFit: z.enum(["contain", "cover", "fill", "none", "scale-down"]).default("cover"),
  autoCalculateVideoDuration: z.boolean().default(false).describe("Automatically calculate duration from video length (overrides videoDuration)"),
  videoCrop: z.object({
    x: z.number(),
    y: z.number(),
    width: z.number(),
    height: z.number(),
    sourceWidth: z.number().optional(),
    sourceHeight: z.number().optional(),
  }).nullable().optional().describe("Optional crop rect for the video"),
  
  // Sequence durations
  fourPanelDuration: z.number().default(450),
  transitionDuration: z.number().default(30),
  popupSequenceDuration: z.number().default(200),
  magnifyingGlassDuration: z.number().default(300),
  videoDuration: z.number().default(400).describe("Duration in frames (ignored if autoCalculateVideoDuration is true)"),
});
const Furniture = ({
  // FourPanelReveal props
  imageSrc = "1.jpeg",
  phase1Start = 30,
  phase1Duration = 30,
  phase2Start = 90,
  phase2Duration = 30,
  phase3Start = 150,
  phase3Interval = 30,
  phase3Duration = 20,
  blurAmount = 10,
  pulseScale = 1.2,
  flashIntensity = 0.8,
  flashDuration = 8,
  
  // PopUpText props
  popupTitle = "Amazing Reveal!",
  popupSubtitle = "Four Panel Effect",
  popupStartFrame = 30,
  popupInDuration = 20,
  popupVisibleForFrames = 120,
  popupOutDuration = 15,
  popupX = 80,
  popupY = 80,
  popupTitleFontSize = 58,
  popupSubtitleFontSize = 36,
  popupTitleColor = "#ffffff",
  popupSubtitleColor = "#b0b0b0",
  popupBackgroundColor = "rgba(0, 0, 0, 0.6)",
  popupPadding = "24px 32px",
  popupBorderRadius = "16px",
  popupFontFamily = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  popupBackdropBlur = true,
  popupMaxWidth = 600,
  
  // MagnifyingGlass props and toggle
  includeMagnifyingGlass = false,
  magnifierX = 50,
  magnifierY = 50,
  magnifierRadius = 200,
  magnificationLevel = 2.5,
  magnifierBorderWidth = 4,
  magnifierBorderColor = "#ffffff",
  magnifierShadowBlur = 20,
  magnifierShadowColor = "rgba(0, 0, 0, 0.3)",
  backgroundZoomStart = 1,
  backgroundZoomEnd = 1.5,
  backgroundZoomDuration = 200,
  magnifyingGlassEasingType = "easeOut",
  
  // Video props and toggle
  includeVideo = false,
  videoSrc = "0902.mp4",
  videoStartFrom = 0,
  videoEndAt = undefined,
  videoPlaybackRate = 1,
  videoBackground = false,
  videoMuted = true,
  videoLoop = false,
  videoObjectFit = "cover",
  autoCalculateVideoDuration = false,
  videoCrop = null,
  
  // Sequence durations
  fourPanelDuration = 300,
  transitionDuration = 30,
  magnifyingGlassDuration = 300,
  videoDuration = 400,
}) => {
  // Helper function to get video source (local vs remote)
  const getMediaSrc = (src) => {
    return resolveMediaSrc(src);
  };

  // Track video error state
  const [videoError, setVideoError] = useState(false);
  const [videoErrorMessage, setVideoErrorMessage] = useState('');

  // Calculate actual video duration if auto-calculation is enabled
  const actualVideoDuration = useMemo(() => {
    if (!autoCalculateVideoDuration || !includeVideo) {
      return videoDuration;
    }
    
    // For auto-calculation, we'd need to use getVideoMetadata in a composition's calculateMetadata
    // For now, return the manual duration as fallback
    // In practice, you'd implement this in Root.tsx using calculateMetadata
    console.warn('Auto video duration calculation requires implementation in composition calculateMetadata');
    return videoDuration;
  }, [autoCalculateVideoDuration, includeVideo, videoDuration, videoSrc]);

  // Handle video errors
  const handleVideoError = (error) => {
    console.error('Video playback error:', error);
    setVideoError(true);
    setVideoErrorMessage(error?.message || 'Failed to load video');
  };

  return (
    <TransitionSeries>
      {/* First sequence: FourPanelReveal */}
      <TransitionSeries.Sequence durationInFrames={fourPanelDuration}>
        <FourPanelReveal
          imageSrc={imageSrc}
          phase1Start={phase1Start}
          phase1Duration={phase1Duration}
          phase2Start={phase2Start}
          phase2Duration={phase2Duration}
          phase3Start={phase3Start}
          phase3Interval={phase3Interval}
          phase3Duration={phase3Duration}
          blurAmount={blurAmount}
          pulseScale={pulseScale}
          flashIntensity={flashIntensity}
          flashDuration={flashDuration}
        />

        {/* PopUpText overlay */}
        <PopUpText
          title={popupTitle}
          subtitle={popupSubtitle}
          startFrame={popupStartFrame}
          inDuration={popupInDuration}
          visibleForFrames={popupVisibleForFrames}
          outDuration={popupOutDuration}
          x={popupX}
          y={popupY}
          titleFontSize={popupTitleFontSize}
          subtitleFontSize={popupSubtitleFontSize}
          titleColor={popupTitleColor}
          subtitleColor={popupSubtitleColor}
          backgroundColor={popupBackgroundColor}
          padding={popupPadding}
          borderRadius={popupBorderRadius}
          fontFamily={popupFontFamily}
          backdropBlur={popupBackdropBlur}
          maxWidth={popupMaxWidth}
        />
      </TransitionSeries.Sequence>

      {/* Conditionally add Video sequence */}
      {includeVideo && (
        <>
          {/* Transition to Video */}
          <TransitionSeries.Transition
            presentation={fade()}
            timing={linearTiming({ durationInFrames: transitionDuration })}
          />

          {/* Fourth sequence: Video */}
          <TransitionSeries.Sequence durationInFrames={actualVideoDuration}>
            <AbsoluteFill>
              {videoError ? (
                // Show error fallback instead of crashing
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#000",
                    color: "#fff",
                    fontFamily: "Arial, sans-serif",
                    padding: "40px",
                    textAlign: "center",
                  }}
                >
                  <div style={{ fontSize: 48, marginBottom: 20 }}>⚠️</div>
                  <div style={{ fontSize: 24, marginBottom: 10 }}>Video Error</div>
                  <div style={{ fontSize: 16, color: "#999", maxWidth: 600 }}>
                    {videoErrorMessage || 'Unable to load video'}
                  </div>
                  <div style={{ fontSize: 14, color: "#666", marginTop: 20 }}>
                    Source: {videoSrc}
                  </div>
                </div>
              ) : videoBackground ? (
                <Blur
                  background={
                    <CroppedVideo
                      src={getMediaSrc(videoSrc)}
                      crop={videoCrop}
                      startFrom={videoStartFrom}
                      endAt={videoEndAt}
                      playbackRate={videoPlaybackRate}
                      muted={videoMuted}
                      loop={videoLoop}
                      onError={handleVideoError}
                      style={{
                        width: "110%",
                        height: "100%",
                        objectFit: 'cover',
                      }}
                      containerStyle={{
                        width: "100%",
                        height: "100%",
                      }}
                    />
                  }
                >
                  <CroppedVideo
                    src={getMediaSrc(videoSrc)}
                    crop={videoCrop}
                    startFrom={videoStartFrom}
                    endAt={videoEndAt}
                    playbackRate={videoPlaybackRate}
                    muted={videoMuted}
                    loop={videoLoop}
                    onError={handleVideoError}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: 'contain',
                    }}
                    containerStyle={{
                      width: "100%",
                      height: "100%",
                    }}
                  />
                </Blur>
              ) : (
                <CroppedVideo
                  src={getMediaSrc(videoSrc)}
                  crop={videoCrop}
                  startFrom={videoStartFrom}
                  endAt={videoEndAt}
                  playbackRate={videoPlaybackRate}
                  muted={videoMuted}
                  loop={videoLoop}
                  onError={handleVideoError}
                  style={{
                    width: "110%",
                    height: "100%",
                    objectFit: videoObjectFit,
                  }}
                  containerStyle={{
                    width: "100%",
                    height: "100%",
                  }}
                />
              )}
            </AbsoluteFill>
          </TransitionSeries.Sequence>
        </>
      )}

      {/* Conditionally add MagnifyingGlass sequence */}
      {includeMagnifyingGlass && (
        <>
          {/* Transition to MagnifyingGlass */}
          <TransitionSeries.Transition
            presentation={fade()}
            timing={linearTiming({ durationInFrames: transitionDuration })}
          />

          {/* Third sequence: MagnifyingGlass */}
          <TransitionSeries.Sequence durationInFrames={magnifyingGlassDuration}>
            <MagnifyingGlass
              imageSrc={imageSrc}
              magnifierX={magnifierX}
              magnifierY={magnifierY}
              magnifierRadius={magnifierRadius}
              magnificationLevel={magnificationLevel}
              borderWidth={magnifierBorderWidth}
              borderColor={magnifierBorderColor}
              shadowBlur={magnifierShadowBlur}
              shadowColor={magnifierShadowColor}
              backgroundZoomStart={backgroundZoomStart}
              backgroundZoomEnd={backgroundZoomEnd}
              backgroundZoomDuration={backgroundZoomDuration}
              easingType={magnifyingGlassEasingType}
            />
          </TransitionSeries.Sequence>
        </>
      )}
    </TransitionSeries>
  );
};

export default Furniture;
;
    var _remotion_globalVariableA, _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
